import { useEffect, useState, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const STORAGE_KEY = "akshay_intro_seen_v8";

export const LoadingScreen = () => {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return true;
    try {
      return !sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return true;
    }
  });

  const [pct, setPct] = useState(0);

  const fillClipRef = useRef(null);
  const brandWrapRef = useRef(null);
  const loadLabelRef = useRef(null);
  const preloaderRef = useRef(null);

  useEffect(() => {
    if (!visible) return undefined;

    document.body.style.overflow = "hidden";
    document.body.classList.add("loading-active");
    if (window.lenis) {
      window.lenis.stop();
    }
    const mainContent = document.querySelector("main");
    if (mainContent) {
      gsap.set(mainContent, { opacity: 0, scale: 1.08 });
    }

    /* Wave config */
    const WAVE_FREQ = 2.5;
    const WAVE_AMP_MAX = 0.045;
    let waveOffset = 0;
    let progressVal = { value: 0 };

    /* Scroll the wave */
    const tickWaveOffset = () => {
      waveOffset += 0.007;
    };
    gsap.ticker.add(tickWaveOffset);

    /* Build CSS polygon clip-path with wave top edge */
    const applyWave = (fill) => {
      if (!fillClipRef.current) return;
      const steps = 60;
      const y = 1 - fill;
      const proximity = Math.min(fill, 1 - fill);
      const amp = WAVE_AMP_MAX * Math.min(proximity * 8, 1);
      let pts = "0% 100%, ";

      for (let i = 0; i <= steps; i++) {
        const x = i / steps;
        const wy = y + amp * Math.sin((x * WAVE_FREQ + waveOffset) * Math.PI * 2);
        pts += `${(x * 100).toFixed(2)}% ${(wy * 100).toFixed(2)}%, `;
      }
      pts += "100% 100%";
      fillClipRef.current.style.clipPath = `polygon(${pts})`;
    };

    /* Render wave every tick */
    const tickWaveRender = () => {
      if (progressVal.value > 0 && progressVal.value < 1) {
        applyWave(progressVal.value);
      }
    };
    gsap.ticker.add(tickWaveRender);

    /* Main fill animation */
    const mainTween = gsap.to(progressVal, {
      value: 1,
      duration: 3.5,
      ease: "power1.inOut",
      onUpdate() {
        setPct(Math.floor(progressVal.value * 100));
      },
      onComplete() {
        setPct(100);
        if (fillClipRef.current) {
          fillClipRef.current.style.clipPath = "inset(0% 0px 0px 0px)";
        }
        
        // Exit sequence
        const tl = gsap.timeline({
          onComplete: () => {
            try {
              sessionStorage.setItem(STORAGE_KEY, "1");
            } catch (e) {}
            setVisible(false);
            document.body.style.overflow = "";
            document.body.classList.remove("loading-active");
            if (mainContent) {
              mainContent.style.opacity = "";
              mainContent.style.transform = "";
            }
            ScrollTrigger.refresh();
            if (window.lenis) {
              window.lenis.start();
            }
          }
        });

        tl.to({}, { duration: 0.5 })
          .to(loadLabelRef.current, {
            opacity: 0,
            y: 6,
            duration: 0.3,
            ease: "power2.in"
          }, "<")
          .to(brandWrapRef.current, {
            scale: 1.35,
            opacity: 0,
            duration: 0.85,
            ease: "power3.inOut"
          }, "-=0.1");

        if (mainContent) {
          tl.fromTo(mainContent, 
            { opacity: 0, scale: 1.08 },
            {
              opacity: 1,
              scale: 1,
              duration: 0.85,
              ease: "power3.out"
            }, 
            "-=0.65"
          );
        }

        tl.to(preloaderRef.current, {
          opacity: 0,
          duration: 0.4,
          ease: "power2.out"
        }, "-=0.3");
      }
    });

    return () => {
      document.body.classList.remove("loading-active");
      gsap.ticker.remove(tickWaveOffset);
      gsap.ticker.remove(tickWaveRender);
      mainTween.kill();
      document.body.style.overflow = "";
      if (window.lenis) {
        window.lenis.start();
      }
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      ref={preloaderRef}
      id="preloader"
      className="fixed inset-0 bg-[#020410] z-[9999] flex flex-col items-center justify-center gap-10"
    >
      <div 
        className="brand-wrap relative line-height-[0.9] origin-center will-change-transform" 
        ref={brandWrapRef}
      >
        <span 
          className="text-dim block font-display font-black tracking-tighter text-[#1b1c26] select-none whitespace-nowrap leading-[0.9]"
          style={{ fontSize: "clamp(2.2rem, 9.5vw, 8rem)" }}
          aria-hidden="true"
        >
          AKSHAY SAWANT
        </span>
        <div 
          ref={fillClipRef} 
          className="text-fill-clip absolute inset-0 will-change-[clip-path]"
        >
          <span 
            className="text-fill block font-display font-black tracking-tighter text-white select-none whitespace-nowrap leading-[0.9]"
            style={{ fontSize: "clamp(2.2rem, 9.5vw, 8rem)" }}
          >
            AKSHAY SAWANT
          </span>
        </div>
      </div>
      
      <div 
        ref={loadLabelRef} 
        className="loading-label font-mono uppercase tracking-[0.32em] text-[0.72rem] text-white/40 flex items-center gap-3 will-change-opacity"
      >
        <span>loading</span>
        <span className="text-white/80 tabular-nums font-medium">{pct} %</span>
      </div>
    </div>
  );
};

export default LoadingScreen;
